export interface WatchlistBaseModel {
  score: number;
  level?: number;
  record: WatchlistRecordModel;
  timestamp?: number;
  isSafeListed?: boolean;
}

export interface WatchlistRecordModel {
  id: string;
  createdAt: number;
  recordType: string;
  records: WatchlistModel[];
}

export interface WatchlistModel {
  lastUpdated: number;
  watchlistId: string;
  watchlistRecord: WatchlistIndividualRecord | WatchlistEntityRecord;
}

export interface WatchlistIndividualRecord {
  type: string;
  names: WatchlistNameModel[];
  gender: string;
  categories: string[];
  images: string[];
  remarks: string[];
  source: string;
  uniqueId: string;
  addresses: string[];
  positions: WatchlistPositionModel[];
  sanctions: WatchlistSanctionModel[];
  educations: WatchlistEducationModel[];
  recordType: string;
  citizenships: string[];
  datesOfBirth: WatchlistDateModel[];
  datesOfDeath: WatchlistDateModel[];
  placesOfBirth: string[];
  relationships: WatchlistRelationshipModel[];
  identifications: WatchlistIdentificationModel[];
}

export interface WatchlistEntityRecord {
  type: string;
  names: WatchlistName[];
  categories: string[];
  entityType: string;
  remarks: string[];
  source: string;
  uniqueId: string;
  addresses: string[];
  sanctions: WatchlistSanctionModel[];
  recordType: string;
  datesOfRegistration: WatchlistDateModel[];
  identifiers: WatchlistIdentificationModel[];
}

export interface WatchlistSanctionModel {
  authority: string;
  program: string;
  startDate: WatchlistDateModel;
  endDate: WatchlistDateModel;
  authorityIssuedId: string;
  scopes: Record<string, string>;
  reason: string;
  source?: string;
}

export interface WatchlistRelationshipModel {
  type: string;
  id: string;
  value: string;
  startDate: WatchlistDateModel;
  endDate: WatchlistDateModel;
  source?: string;
  watchlistId?: string;
}

export interface WatchlistEducationModel {
  name: string;
  startDate: WatchlistDateModel;
  endDate: WatchlistDateModel;
  source?: string;
}

export interface WatchlistPositionModel {
  name: string;
  institution: string;
  startDate: WatchlistDateModel;
  endDate: WatchlistDateModel;
  source?: string;
}

export interface WatchlistIdentificationModel {
  value: string;
  type: string;
  issuer: string;
  dateOfIssue: WatchlistDateModel;
  expireDate: WatchlistDateModel;
  placeOfIssue: string;
  source?: string;
}

export interface WatchlistNameModel {
  parts: string[];
  value: string;
  nameType: WatchlistNameType;
}

export interface WatchlistDateModel {
  day: number;
  month: number;
  year: number;
}

export interface WatchlistListsModel {
  id: string;
  name: string;
  country: string;
  authority: string;
  authorityWebsite: string;
  categories: string[];
  lastChangedDate: number;
  lastCheckedDate: number;
  size: number;
}

export enum WatchlistNameType {
  PRIMARY = 'PRIMARY',
  ALIAS = 'ALIAS',
  SCRIPT = 'SCRIPT',
}

export interface WatchlistGeneralRequest {
  id: string | null;
  threshold: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  date: string | null;
  lists: string[];
  referenceId: string | null;
}

export interface WatchlistIndividualRequest {
  id: string | null;
  threshold: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  identification: string | null;
  dateOfBirth: string | null;
  citizenship: string | null;
  lists: string[];
  referenceId: string | null;
}

export interface WatchlistEntityRequest {
  id: string | null;
  threshold: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  identifier: string | null;
  dateOfRegistration: string | null;
  lists: string[];
  referenceId: string | null;
}

export interface WatchlistName {
  value: string;
  nameType: string;
}

export interface WatchlistConfiguration {
  id: string;
  name: string;
  watchlists: string[];
}

export interface SafeListRequest {
  referenceId: string;
  consolidatedRecordIds: string[];
  individualMatchRequest?: WatchlistIndividualRequest | null;
  entityMatchRequest?: WatchlistEntityRequest | null;
  generalMatchRequest?: WatchlistGeneralRequest | null;
}

export interface SafeListTableRow {
  consolidatedRecordId: string;
  record: WatchlistBaseModel | null;
}

export interface SafeListConfig {
  id: string;
  enabled: boolean;
}

export interface WatchlistEngineDataWrapper<T> {
  data: T;
}

export interface WatchlistProxyResponse<T> {
  status: number;
  data: T | undefined;
  error: boolean;
}

export type WatchlistByIdResponse = WatchlistProxyResponse<WatchlistEngineDataWrapper<WatchlistBaseModel['record']>>;

